import {api, basicAuthHeaderForCredentials, handleApiError, ServiceResponse} from "../ApiClient";
import {SessionService} from "./SessionService";

export type Credentials = {
    email: string
    password: string
}

export interface CreateCredentialsResponse extends ServiceResponse {
    credentials?: Credentials
}

export const CredentialsService = () => {

    const { createSession } = SessionService();

    const createCredentials = async (credentials: Credentials): Promise<CreateCredentialsResponse> => {
            const errors = validateInput(credentials.email, credentials.password);
            if (errors) {
                return {
                    error: {
                        message: errors
                    }
                }
            }
            return api.post<void>(`/api/1/credentials`, {}, {
                withCredentials: true,
                headers: {
                    Authorization: basicAuthHeaderForCredentials(credentials)
                }
            })
                .then((r) => {
                    if (r.status !== 202) {
                        return {
                            error: {
                                message: 'Error registering.'
                            }
                        }
                    }

                    return {
                        credentials: credentials
                    };
                })
                .catch(handleApiError)
        }

    const updateCredentials = async (credentials: Credentials, emailCode: string): Promise<ServiceResponse> => {
            let errors = validateInput(credentials.email, credentials.password);
            errors += emailCode ? "" : "No email confirmation code provided.\n";
            if (errors) {
                return {
                    error: {
                        message: errors
                    }
                }
            }

            return api.put<void>(`/api/1/credentials?confirmationCode=${emailCode}`, {}, {
                withCredentials: true,
                headers: {
                    Authorization: basicAuthHeaderForCredentials(credentials)
                }
            })
                .then((r) => {
                    if (r.status === 202) {
                        return {}
                    }
                    return {
                        error: {
                            message: 'Server did not respond successfully.'
                        }
                    };
                })
                .catch(handleApiError)
        }

    const confirmEmail = async (credentials: Credentials, code: string): Promise<ServiceResponse> => {
            try {
                await api.put<void>(`/api/1/confirmation?confirmationCode=${code}`, {}, {
                    withCredentials: true,
                    headers: {
                        Authorization: basicAuthHeaderForCredentials(credentials)
                    }
                });
                return await createSession(credentials);
            } catch (err) {
                return handleApiError(err);
            }
        }

    const resendConfirmation = async (credentials: Credentials): Promise<ServiceResponse> => {
        return api.delete<void>(`/api/1/confirmation`, {
            withCredentials: true,
            headers: {
                Authorization: basicAuthHeaderForCredentials(credentials)
            }
        })
            .then((resp) => {
                if (resp.status === 200) {
                    return {}
                }

                return {
                    error: {
                        message: 'Could not resend confirmation code.'
                    }
                }
            })
            .catch(handleApiError)

    }

    const deleteCredentials = (email: string): Promise<ServiceResponse> => {
        return api.delete<void>(`/api/1/credentials`, {
            data: {
                email
            }
        }).then((resp) => {
            if (resp.status !== 202) {
                return {
                    error: {
                        message: 'Could not reset your password.'
                    }
                }
            }
            return {}
        }).catch(handleApiError)
    }

    const validateInput = (email: string, password: string): string => {
        // this isn't the best, but better than another dependency... right?
        const emailValidation: RegExp = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/
        const passwordValidation: RegExp = /^(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$/
        let errors = '';
        if (!emailValidation.test(email.toLowerCase())) {
            errors += "Not a valid email address.\n"
        }

        if (!passwordValidation.test(password)) {
            errors += "Password should contain, at least: 8 characters, 1 uppercase letter, 1 lowercase letter, 1 number\n"
        }

        return errors;
    }

    return {
        createCredentials,
        confirmEmail,
        resendConfirmation,
        deleteCredentials,
        updateCredentials
    }
}
